import { redirectCustomDomains, sveltekitApiUrl } from "app/utils/urls";
import type { LayoutLoadEvent } from "./$types";
import { type Community, type CommunityPermissions } from "@outverse-video/outverse-api-client-lib";
import { handleApiError } from "$utils/errors";

/** reusable load for community data (used for community layout + join layout)
 *
 * The community data is retrieved from api/community for SSR.
 *
 * The primary use for SSR is to fill out opengraph content and increase SEO ranking.
 *
 * Loads initial community data (using users auth token if available), and determines the community slug
 * based on either the subdomain or the community path parameter.
 *
 * This needs to be proxied via a svelte fetch in order to make use of a browser-based cookie auth token set
 * by our firebase auth provider. We can't use the firebase auth as it does not use cookies (uses local storage).
 *
 * The cookie should have been set when the client firebase auth was last successfully run (page load).
 * See `store/auth.ts` and `/token` endpoint.
 *
 */

export const loadCommunity = async ({ fetch, params, route, url }: LayoutLoadEvent) => {
  const communitySlug = params.community;
  // check and redirect communities with a custom domain
  redirectCustomDomains(route, url, communitySlug);

  const baseApiUrl = sveltekitApiUrl();

  // fetch data in parallel, kit awaits all promises before returning
  const [aiToken, community, permissions] = await Promise.all([
    fetch(`${baseApiUrl}/ai`, { method: "POST" }).then((r) => (r.ok ? r.json() : null)),
    fetch(`${baseApiUrl}/community/${communitySlug}`)
      .then(handleApiError)
      .then((response) => {
        return response.json();
      }) as Promise<Community>,
    fetch(`${baseApiUrl}/community/${communitySlug}/permissions`)
      .then(handleApiError)
      .then((response) => {
        return response.json() as Promise<CommunityPermissions>;
      }),
  ]);

  return {
    // fill in opengraph content
    og: {
      title: community.name,
      description: community.about,
      image: community.coverImage,
      icon: community.logoImage,
    },
    hideSidebar: false, // hide sidebar on some community pages (settings)
    communitySlug,
    community,
    permissions: permissions.permissions || [],
    aiToken: aiToken,
    isKnowledgeBase: false,
    isChromeExtension: false,
  };
};
